import type { ClientOrganization } from '@DigitaleDoerfer/digitale-doerfer-api';
import { useCallback, useMemo, useState } from 'react';

const MIN_SEARCH_LENGTH = 3;

type Params = {
  organizations: ClientOrganization[] | undefined;
  onSelect: (id: string) => void;
  onDismiss: () => void;
  onCreatePress?: () => void;
};

/**
 * Encapsulates the organization search state: the query, the filtered results
 * (only once the query reaches {@link MIN_SEARCH_LENGTH} characters) and the
 * select / dismiss / create handlers, all of which reset the query on close.
 */
export const useOrganizationSearch = ({
  organizations,
  onSelect,
  onDismiss,
  onCreatePress
}: Params) => {
  const [search, setSearch] = useState<string>('');

  const handleDismiss = useCallback(() => {
    setSearch('');
    onDismiss();
  }, [onDismiss]);

  const handleSelect = useCallback(
    (id: string) => {
      onSelect(id);
      handleDismiss();
    },
    [onSelect, handleDismiss]
  );

  const handleCreatePress = useCallback(() => {
    handleDismiss();
    onCreatePress?.();
  }, [handleDismiss, onCreatePress]);

  const filtered = useMemo(() => {
    if (!organizations) return [];
    const query = search.trim().toLowerCase();
    if (query.length < MIN_SEARCH_LENGTH) return [];
    return organizations.filter((org) =>
      org.name?.toLowerCase().includes(query)
    );
  }, [organizations, search]);

  const showHint = search.trim().length < MIN_SEARCH_LENGTH;

  return {
    search,
    setSearch,
    filtered,
    showHint,
    handleDismiss,
    handleSelect,
    handleCreatePress
  };
};
